import React, { useState } from "react";
import {TextField, PrimaryButton, Stack, MessageBar, MessageBarType, Label} from "@fluentui/react";
import {
    useGetMyGraphUserQuery,
    usePatchMyGraphUserMutation,
    useSetMyUserImageMutation
} from "../../store/endpoints/GraphUserEndpoints";
import { IGraphUser } from "../../models/GraphUser";
import ImageUploader from "../../components/Inputs/ImageUploader";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";

const UserDetailsPage: React.FC = () => {
    const supportSession = useSelector((state: RootState) => state.auth.supportSession);
    if (supportSession)
        return (
            <MessageBar messageBarType={MessageBarType.info} styles={{root: {margin: '1em'}}}>
                {`You're in a support session as ${supportSession.actor}, which has no sign-in profile to edit here.`}
            </MessageBar>
        );
    return <MyUserDetails/>;
};

const MyUserDetails: React.FC = () => {
    const { data: user, isLoading, isError, refetch } = useGetMyGraphUserQuery();
    const [patchUser, { isLoading: isSaving, isError: isSaveError, error: saveError }] = usePatchMyGraphUserMutation();
    const [updateUserImage] = useSetMyUserImageMutation();
    const [workingUser, setWorkingUser] = useState<Partial<IGraphUser | undefined>>(user);
    const [success, setSuccess] = useState(false);

    React.useEffect(() => {
        setWorkingUser(user);
    }, [user]);

    const update = (fields: Partial<typeof user>) => {
        setWorkingUser(prev => ({ ...prev, ...fields }));
    };

    const handleSave = async () => {
        setSuccess(false);
        if (!workingUser) return;
        await patchUser({ request: workingUser }).unwrap();
        setSuccess(true);
        refetch();
    };

    if (isLoading) return <div>Loading...</div>;
    if (isError) return <MessageBar messageBarType={MessageBarType.error}>Failed to load user details.</MessageBar>;

    // Responsive styles for two columns
    const containerStyles = {
        root: {
            margin: "0 auto",
            padding: 24
        }
    };
    const columnStyles = {
        root: {
            width: '100%',
            maxWidth: 400,
        }
    };
    const horizontalStackStyles = {
        root: {
            display: 'flex',
            flexDirection: 'row',
            gap: 32,
            flexWrap: 'wrap',
        }
    };

    return (
        <div>
            <Stack tokens={{ childrenGap: 16 }} styles={containerStyles}>
                <h2>User Details</h2>
                {isSaveError && <MessageBar messageBarType={MessageBarType.error}>{saveError?.toString()}</MessageBar>}
                {success && <MessageBar messageBarType={MessageBarType.success}>Profile updated successfully.</MessageBar>}
                <Stack horizontal className={'user-details'} styles={horizontalStackStyles}>
                    <Stack styles={columnStyles} tokens={{ childrenGap: 16 }}>
                        <Label>Profile Image</Label>
                        <div style={{border: !workingUser?.profilePicture ? undefined: '2px solid #ddd', padding: '10px', borderRadius: '5px', display: 'inline-block'}}>
                            <ImageUploader 
                                value={workingUser?.profilePicture}
                                onChange={(url) => update({ profilePicture: url })}
                                onClear={() => update({ profilePicture: undefined })}
                                customUploader={async (req) => {
                                    const data = await updateUserImage({ imageId: 'myuser-profile', imageType: 'profile', file: req.file }).unwrap();
                                    return { data };
                                }}
                            />
                        </div>
                        <div data-setting-id='myUser.profile.displayName'><TextField label="Display Name" value={workingUser?.displayName ?? ""} onChange={(_, v) => update({ displayName: v })} /></div>
                        <div data-setting-id='myUser.profile.givenName'><TextField label="Given Name" value={workingUser?.givenName ?? ""} onChange={(_, v) => update({ givenName: v })} /></div>
                        <div data-setting-id='myUser.profile.surname'><TextField label="Surname" value={workingUser?.surname ?? ""} onChange={(_, v) => update({ surname: v })} /></div>
                        <div data-setting-id='myUser.profile.jobTitle'><TextField label="Job Title" value={workingUser?.jobTitle ?? ""} onChange={(_, v) => update({ jobTitle: v })} /></div>
                        <div data-setting-id='myUser.profile.department'><TextField label="Department" value={workingUser?.department ?? ""} onChange={(_, v) => update({ department: v })} /></div>
                        <div data-setting-id='myUser.profile.companyName'><TextField label="Company Name" value={workingUser?.companyName ?? ""} onChange={(_, v) => update({ companyName: v })} /></div>
                    </Stack>
                    <Stack styles={columnStyles} className={'user-details'} tokens={{ childrenGap: 16 }}>
                        <div data-setting-id='myUser.profile.officePhone'><TextField label="Office Phone" value={workingUser?.officePhone ?? ""} onChange={(_, v) => update({ officePhone: v })} /></div>
                        <div data-setting-id='myUser.profile.mobilePhone'><TextField label="Mobile Phone" value={workingUser?.mobilePhone ?? ""} onChange={(_, v) => update({ mobilePhone: v })} /></div>
                        <div data-setting-id='myUser.profile.preferredLanguage'><TextField label="Preferred Language" value={workingUser?.preferredLanguage ?? ""} onChange={(_, v) => update({ preferredLanguage: v })} /></div>
                        <div data-setting-id='myUser.profile.officeLocation'><TextField label="Office Location" value={workingUser?.officeLocation ?? ""} onChange={(_, v) => update({ officeLocation: v })} /></div>
                        <div data-setting-id='myUser.address.streetAddress'><TextField label="Street Address" value={workingUser?.streetAddress ?? ""} onChange={(_, v) => update({ streetAddress: v })} /></div>
                        <div data-setting-id='myUser.address.city'><TextField label="City" value={workingUser?.city ?? ""} onChange={(_, v) => update({ city: v })} /></div>
                        <div data-setting-id='myUser.address.state'><TextField label="State" value={workingUser?.state ?? ""} onChange={(_, v) => update({ state: v })} /></div>
                        <div data-setting-id='myUser.address.country'><TextField label="Country" value={workingUser?.country ?? ""} onChange={(_, v) => update({ country: v })} /></div>
                        <div data-setting-id='myUser.address.postalCode'><TextField label="Postal Code" value={workingUser?.postalCode ?? ""} onChange={(_, v) => update({ postalCode: v })} /></div>
                    </Stack>
                </Stack>
    
            </Stack>
            <PrimaryButton text="Save" onClick={handleSave} disabled={isSaving} />
        </div>
    );
};

export default UserDetailsPage;
